import React, { useState, useRef, useEffect } from "react";
import { updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase-config";
import {
  Button, Table, TableBody, TableCell, TableRow, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TableContainer,
  TableHead, IconButton, TextField, FormControl, InputLabel, Select,
  MenuItem, Box, Chip, Divider, Paper, alpha, useTheme, Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment
} from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import html2pdf from "html2pdf.js";
import appTheme from "../theme";

const InvoiceModal = ({ invoice, isModalOpen, handleClose }) => {
  // ✅ ADD RESPONSIVE HOOKS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [editableInvoice, setEditableInvoice] = useState(invoice);
  const [editingIndex, setEditingIndex] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [editField, setEditField] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const invoiceRef = useRef();

  // 💰 DISCOUNT STATES - ADD THESE 4 LINES
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [originalAmount, setOriginalAmount] = useState(0);

  console.log(invoice);

  // 💰 DISCOUNT HELPERS - ADD THESE
  const calculateDiscountedTotal = (originalTotal = 0) => {
    if (discountType === 'none' || !discountValue || originalTotal === 0) {
      return originalTotal;
    }
    const discount = parseFloat(discountValue) || 0;
    if (discountType === 'percent') {
      return originalTotal * (1 - discount / 100);
    }
    return originalTotal - discount;
  };

  useEffect(() => {
    if (editableInvoice?.totalPrice) {
      setOriginalAmount(editableInvoice.totalPrice);
    }
  }, [editableInvoice?.totalPrice]);


  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(decimals);
  };

  const safeNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return Number(value);
  };

  useEffect(() => {

    setEditableInvoice(invoice);
    setDiscountType(invoice.discountType || 'none')
    setDiscountValue(invoice.discountValue || '')
    setDiscountReason(invoice.discountReason || '')
    setOriginalAmount(invoice.totalPrice || 0)
  }, [invoice]);

  // ✅ ENHANCED VAT CALCULATION FUNCTION
  const calculateVATForItem = (priceExclVAT, hasVAT) => {
    const basePrice = safeNumber(priceExclVAT);
    const vatRate = 0.20; // 20% VAT

    if (!hasVAT || basePrice <= 0) {
      return {
        priceExclVAT: basePrice,
        vatAmount: 0,
        priceInclVAT: basePrice,
        hasVAT: false
      };
    }

    const vatAmount = basePrice * vatRate;
    const priceInclVAT = basePrice + vatAmount;

    return {
      priceExclVAT: Number(basePrice.toFixed(2)),
      vatAmount: Number(vatAmount.toFixed(2)),
      priceInclVAT: Number(priceInclVAT.toFixed(2)),
      hasVAT: true
    };
  };

  const calculateItemTotal = (item) => {
    const quantity = safeNumber(item.quantity);
    const priceInclVAT = safeNumber(item.priceInclVAT || item.price);
    return quantity * priceInclVAT;
  };

  const calculateInvoiceTotals = () => {
    let subtotal = 0;
    let totalVAT = 0;

    editableInvoice.items.forEach(item => {
      const quantity = safeNumber(item.quantity);
      const priceExclVAT = safeNumber(item.priceExclVAT || item.price);
      const vatAmount = safeNumber(item.vatAmount);

      subtotal += quantity * priceExclVAT;
      totalVAT += quantity * vatAmount;
    });

    return {
      subtotal,
      tax: totalVAT,
      totalPrice: subtotal + totalVAT
    };
  };

  const handleStatusChange = (field, value) => {
    setEditableInvoice((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateTotalPrice = () => {
    const totals = calculateInvoiceTotals();
    setEditableInvoice((prev) => ({
      ...prev,
      ...totals
    }));
  };
  // ✅ FIXED: Function to calculate totals for any invoice object
  const calculateInvoiceTotalsForInvoice = (invoiceObj) => {
    let subtotal = 0;
    let totalVAT = 0;

    invoiceObj.items.forEach(item => {
      const quantity = safeNumber(item.quantity);
      const priceExclVAT = safeNumber(item.priceExclVAT || item.price);
      const vatAmount = safeNumber(item.vatAmount);

      subtotal += quantity * priceExclVAT;
      totalVAT += quantity * vatAmount;
    });

    return {
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(totalVAT.toFixed(2)),
      totalPrice: Number((subtotal + totalVAT).toFixed(2))
    };
  };

  // ✅ ENHANCED EDIT FIELD HANDLER WITH VAT CALCULATION
  // ✅ FIXED: Single state update with immediate totals calculation
  const handleEditField = (field, index, value) => {
    setEditableInvoice((prevInvoice) => {
      // Create completely new invoice object
      const newInvoice = { ...prevInvoice };

      // Create new items array
      const newItems = [...newInvoice.items];

      // Create new item object
      const currentItem = { ...newItems[index] };

      // Update the field
      currentItem[field] = value;

      // Recalculate VAT if price changed
      if (field === 'priceExclVAT') {
        const vatCalculation = calculateVATForItem(value, currentItem.hasVAT);
        Object.assign(currentItem, vatCalculation);
      }

      // Update the item in the new array
      newItems[index] = currentItem;

      // Update the new invoice
      newInvoice.items = newItems;

      // ✅ FIXED: Calculate totals immediately in the same state update
      const updatedTotals = calculateInvoiceTotalsForInvoice(newInvoice);

      return {
        ...newInvoice,
        ...updatedTotals
      };
    });
  };



  const handleBlur = () => {
    if (editingIndex !== null && editField !== null) {
      let processedValue = inputValue;

      // Process value based on field type
      if (editField === 'quantity' || editField === 'priceExclVAT') {
        processedValue = safeNumber(inputValue);
      } else if (editField === 'hasVAT') {
        processedValue = inputValue === 'true' || inputValue === true;
      }

      handleEditField(editField, editingIndex, processedValue);
      setEditingIndex(null);
      setEditField(null);
      setInputValue("");
    }
  };

  // ✅ ENHANCED EDIT BUTTON COMPONENT
  const EditableField = ({
    value,
    field,
    index,
    type = 'text',
    options = null,
    displayValue = null
  }) => {
    const isEditing = editingIndex === index && editField === field;

    if (isEditing) {
      if (type === 'select') {
        return (
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleBlur}
              autoFocus
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else if (type === 'checkbox') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={inputValue === 'true' || inputValue === true}
              onChange={(e) => setInputValue(e.target.checked)}
              size="small"
            />
            <IconButton
              size="small"
              onClick={handleBlur}
              sx={{ color: appTheme.colors.primary }}
            >
              <CheckIcon sx={{ fontSize: isMobile ? '14px' : '16px' }} />
            </IconButton>
          </Box>
        );
      } else {
        return (
          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            onKeyPress={(e) => e.key === 'Enter' && handleBlur()}
            type={type}
            size="small"
            variant="outlined"
            autoFocus
            sx={{
              width: isMobile ? (type === 'number' ? '80px' : '120px') : 'auto',
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }
            }}
          />
        );
      }
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{
          fontSize: isMobile ? '0.75rem' : '0.875rem'
        }}>
          {displayValue || value}
        </Typography>
        <IconButton
          size="small"
          className="edit-icon"
          onClick={() => {
            setEditingIndex(index);
            setEditField(field);
            setInputValue(type === 'checkbox' ? value : String(value || ''));
          }}
          sx={{
            padding: isMobile ? '2px' : '4px'
          }}
        >
          <EditIcon sx={{ fontSize: isMobile ? '14px' : '16px' }} />
        </IconButton>
      </Box>
    );
  };

  const handleSave = async () => {
    const invoiceDocRef = doc(db, 'invoices', editableInvoice.id)
    try {
      await updateDoc(invoiceDocRef, {
        ...editableInvoice,
        // ✅ SAVE DISCOUNT FIELDS
        discountType,
        discountValue: discountType !== 'none' ? parseFloat(discountValue) || 0 : null,
        discountReason: discountType !== 'none' ? discountReason : null,
        discountedTotal: calculateDiscountedTotal(originalAmount)
      })
      alert('Invoice + Discount updated!')
      handleClose()
    } catch (err) {
      console.error('Error updating invoice', err)
    }
  }


  const handleSendInvoice = () => {
    setTimeout(() => {
      const element = invoiceRef.current;

      // **ENHANCED PDF OPTIONS FOR BETTER LAYOUT**
      const options = {
        margin: [0.5, 0.5, 0.5, 0.5], // Uniform margins
        filename: `Invoice_${editableInvoice.id}.pdf`,
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 1024,
          windowHeight: 768
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          orientation: "portrait",
          compress: true
        },
        pagebreak: {
          mode: ['css'],
        }
      };

      const clone = element.cloneNode(true);

      const discountBlocks = clone.querySelectorAll('.hide-in-pdf');
      discountBlocks.forEach(block => block.remove());

      // **HIDE EDIT ICONS**
      const editIcons = clone.querySelectorAll(".edit-icon");
      editIcons.forEach((icon) => (icon.style.display = "none"));

      // **REPLACE DROPDOWNS WITH TEXT**
      const dropdowns = clone.querySelectorAll("select");
      dropdowns.forEach((dropdown) => {
        const selectedValue = dropdown.options[dropdown.selectedIndex].text;
        const span = document.createElement("span");
        span.textContent = selectedValue;
        span.style.fontSize = "14px";
        span.style.fontWeight = "500";
        dropdown.parentNode.replaceChild(span, dropdown);
      });

      // **REDUCE TABLE ROW HEIGHTS**
      const tableRows = clone.querySelectorAll("tr");
      tableRows.forEach((row) => {
        row.style.height = "auto";
        row.style.minHeight = "32px"; // **Reduced from default 48px**
        row.style.lineHeight = "1.2";
      });

      // **REDUCE TABLE CELL PADDING**
      const tableCells = clone.querySelectorAll("td, th");
      tableCells.forEach((cell) => {
        cell.style.padding = "6px 8px"; // **Reduced padding**
        cell.style.fontSize = "12px"; // **Consistent font size**
        cell.style.lineHeight = "1.3";
        cell.style.verticalAlign = "middle";
      });

      // **MAKE TABLE HEADERS MORE COMPACT**
      const headerCells = clone.querySelectorAll("th");
      headerCells.forEach((header) => {
        header.style.padding = "8px";
        header.style.fontSize = "12px";
        header.style.fontWeight = "bold";
        header.style.lineHeight = "1.2";
        header.style.height = "36px"; // **Fixed header height**
      });

      // **FIX TOTALS CONTAINER - PREVENT PAGE BREAK**
      const totalsContainer = clone.querySelector('[style*="border: 2px solid"]') ||
        clone.querySelector('[style*="backgroundColor"]');

      if (totalsContainer) {
        // **Add page break avoidance classes and styles**
        totalsContainer.classList.add('page-break-avoid');
        totalsContainer.style.pageBreakInside = 'avoid';
        totalsContainer.style.breakInside = 'avoid';
        totalsContainer.style.display = 'block';
        totalsContainer.style.overflow = 'visible';
        totalsContainer.style.minHeight = 'auto';
        totalsContainer.style.maxHeight = 'none';

        // **Ensure parent container also avoids break**
        const parentBox = totalsContainer.parentElement;
        if (parentBox) {
          parentBox.style.pageBreakInside = 'avoid';
          parentBox.classList.add('page-break-avoid');
        }

        // **Make totals section more compact**
        totalsContainer.style.padding = '12px';
        totalsContainer.style.margin = '12px 0';

        // **Reduce font sizes in totals**
        const totalTexts = totalsContainer.querySelectorAll('*');
        totalTexts.forEach(text => {
          if (text.style.fontSize) {
            const currentSize = parseInt(text.style.fontSize);
            if (currentSize > 12) {
              text.style.fontSize = '14px';
            } else if (currentSize <= 12) {
              text.style.fontSize = '12px';
            }
          }
          text.style.lineHeight = '1.4';
        });
      }

      // **MAKE STATUS CONTROLS MORE COMPACT**
      const statusControls = clone.querySelector('[style*="flex: 1"]');
      if (statusControls) {
        statusControls.style.marginBottom = '8px';
        statusControls.style.pageBreakInside = 'avoid';
        statusControls.classList.add('page-break-avoid');
      }

      // **ADD PAGE BREAK BEFORE TOTALS SECTION IF NEEDED**
      const totalsSection = clone.querySelector('[style*="justifyContent: space-between"]');
      if (totalsSection) {
        totalsSection.style.pageBreakInside = 'avoid';
        totalsSection.classList.add('page-break-avoid');

        // **Add a page break class that we can target**
        const pageBreakDiv = document.createElement('div');
        pageBreakDiv.className = 'totals-section-container';
        pageBreakDiv.style.pageBreakInside = 'avoid';
        pageBreakDiv.style.breakInside = 'avoid';
        pageBreakDiv.style.display = 'block';

        // **Wrap the totals section**
        totalsSection.parentNode.insertBefore(pageBreakDiv, totalsSection);
        pageBreakDiv.appendChild(totalsSection);
      }

      // **REDUCE OVERALL SPACING**
      const allBoxes = clone.querySelectorAll('[style*="marginBottom"]');
      allBoxes.forEach(box => {
        if (box.style.marginBottom) {
          const currentMargin = parseInt(box.style.marginBottom);
          if (currentMargin > 16) {
            box.style.marginBottom = '12px';
          }
        }
      });

      // **OPTIMIZE TABLE CONTAINER**
      const tableContainer = clone.querySelector('table');
      if (tableContainer) {
        tableContainer.style.borderCollapse = 'collapse';
        tableContainer.style.width = '100%';
        tableContainer.style.fontSize = '12px';

        // **Ensure table doesn't break awkwardly**
        const tableWrapper = tableContainer.closest('[component="Paper"]') ||
          tableContainer.parentElement;
        if (tableWrapper) {
          tableWrapper.style.pageBreakInside = 'auto';
          tableWrapper.style.overflow = 'visible';
        }
      }

      // **ADD CUSTOM CSS FOR BETTER PDF RENDERING**
      const style = document.createElement('style');
      style.textContent = `
        @media print {

          .hide-in-pdf {
            display: none !important;
          }

          .pdf-root {
            width: 100%;
          }

          /* 🔒 KEEP HEADER + BILL DETAILS TOGETHER */
          .pdf-header-block {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* 🚫 PREVENT TABLE FROM JUMPING TO NEXT PAGE */
          table {
            page-break-before: auto !important;
            break-before: auto !important;
            page-break-inside: auto !important;
            width: 100%;
          }

          /* ✅ ALLOW BREAKS ONLY BETWEEN ROWS */
          tbody tr {
            page-break-inside: avoid !important;
          }

          /* 🔧 PREVENT MUI PAPER FROM CAUSING BREAKS */
          .MuiPaper-root {
            page-break-inside: auto !important;
            break-inside: auto !important;
          }

          /* 🔧 FLEX FIX */
          .MuiBox-root {
            page-break-inside: auto !important;
          }
        }
        `;
      clone.appendChild(style);

      clone.appendChild(style);

      // **GENERATE PDF WITH ENHANCED OPTIONS**
      html2pdf()
        .from(clone)
        .set(options)
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          // **Add page numbers if multiple pages**
          const pageCount = pdf.internal.getNumberOfPages();
          if (pageCount > 1) {
            for (let i = 1; i <= pageCount; i++) {
              pdf.setPage(i);
              pdf.setFontSize(10);
              pdf.text(
                `Page ${i} of ${pageCount}`,
                pdf.internal.pageSize.getWidth() - 50,
                pdf.internal.pageSize.getHeight() - 10
              );
            }
          }
        })
        .save();
    }, 100);
  };


  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : appTheme.borderRadius.rounded,
          maxHeight: isMobile ? '100vh' : '90vh',
          margin: isMobile ? 0 : undefined,
          width: isMobile ? '100vw' : undefined,
        }
      }}
      scroll="paper"
    >
      <DialogTitle sx={{
        background: appTheme.gradients.hero,
        color: "#FFFFFF",
        fontWeight: appTheme.typography.fontWeights.bold,
        padding: isMobile ? theme.spacing(2) : theme.spacing(3),
        fontSize: isMobile ? '1.1rem' : '1.25rem'
      }}>
        📄 Invoice Details
      </DialogTitle>

      <DialogContent sx={{
        padding: 0,
        overflowX: isMobile ? 'hidden' : 'auto',
        pageBreakInside: 'auto'
      }}>
        <Box
          ref={invoiceRef}
          className="pdf-root"
          sx={{
            padding: isMobile ? theme.spacing(1.5) : appTheme.spacing.lg,
            width: '100%',
          }}
        >
          <Box className="pdf-header-block">

            {/* INVOICE HEADER - Same as before */}
            <Box sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              marginBottom: isMobile ? theme.spacing(2) : appTheme.spacing.xl,
              padding: isMobile ? theme.spacing(2) : appTheme.spacing.lg,
              backgroundColor: appTheme.colors.surfaceLuxury,
              borderRadius: appTheme.borderRadius.medium,
              gap: isMobile ? theme.spacing(2) : 0
            }}>
              <Box>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: appTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  Bill From:
                </Typography>
                <Typography variant="body1" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  Crispydosa Kitchen Limited
                </Typography>
                <Typography variant="body2" sx={{
                  color: appTheme.colors.textSecondary,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>
                  20, Portman Road Reading, RG30 1Ea, United Kingdom
                </Typography>
              </Box>
              <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: appTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  Invoice Date:
                </Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  {new Date(invoice.createdAt).toLocaleDateString('en-CA')}
                </Typography>
              </Box>
            </Box>

            {/* CUSTOMER DETAILS - Same as before */}
            <Box sx={{
              marginBottom: isMobile ? theme.spacing(2) : appTheme.spacing.xl,
              padding: isMobile ? theme.spacing(2) : appTheme.spacing.lg,
              backgroundColor: appTheme.colors.surface,
              borderRadius: appTheme.borderRadius.medium,
              border: `1px solid ${appTheme.colors.borderPrimary}`
            }}>
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: appTheme.colors.primary,
                marginBottom: 2,
                fontSize: isMobile ? '1rem' : '1.25rem'
              }}>
                Bill To:
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? 1 : 2
              }}>
                <Box>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}>Name:</Typography>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>{editableInvoice.restaurantName}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}>Email:</Typography>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>{editableInvoice.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}>Address:</Typography>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>{editableInvoice.address}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}>Phone:</Typography>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>{editableInvoice.phone}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          {/* ✅ ENHANCED ITEMS TABLE WITH FULL EDIT FUNCTIONALITY */}
          <TableContainer
            component={Paper}
            sx={{
              marginBottom: isMobile ? theme.spacing(2) : appTheme.spacing.xl,
              borderRadius: appTheme.borderRadius.medium,
              boxShadow: appTheme.shadows.soft,
              overflowX: isMobile ? 'auto' : 'visible',
              maxWidth: '100%'
            }}
          >
            <Table sx={{
              minWidth: isMobile ? 1000 : 'auto'
            }}>
              <TableHead sx={{ backgroundColor: appTheme.colors.primary }}>
                <TableRow>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Title</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Brand</TableCell>
                  {/* <TableCell sx={{ 
                    color: "#FFFFFF", 
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Has VAT</TableCell> */}
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Price (Excl VAT)</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>VAT Amount</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Price (Inc VAT)</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Quantity</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Units</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableInvoice.items.map((item, index) => (
                  <TableRow key={index} sx={{
                    "&:hover": {
                      backgroundColor: alpha(appTheme.colors.primary, 0.05)
                    }
                  }}>
                    {/* ✅ EDITABLE TITLE */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '140px' : 'auto'
                    }}>
                      <EditableField
                        value={item.title}
                        field="title"
                        index={index}
                        type="text"
                      />
                    </TableCell>

                    {/* ✅ EDITABLE BRAND */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '120px' : 'auto'
                    }}>
                      <EditableField
                        value={item.brand}
                        field="brand"
                        index={index}
                        type="text"
                      />
                    </TableCell>

                    {/* ✅ EDITABLE VAT STATUS */}
                    {/* <TableCell sx={{ 
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <EditableField
                        value={item.hasVAT}
                        field="hasVAT"
                        index={index}
                        type="checkbox"
                        displayValue={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.hasVAT ? (
                              <Chip 
                                label="Yes" 
                                size="small" 
                                sx={{
                                  backgroundColor: alpha(appTheme.colors.accent, 0.2),
                                  color: appTheme.colors.accent,
                                  fontSize: isMobile ? '0.5rem' : '0.6rem',
                                  height: isMobile ? '16px' : '20px'
                                }}
                              />
                            ) : (
                              <Chip 
                                label="No" 
                                size="small" 
                                sx={{
                                  backgroundColor: alpha(appTheme.colors.textSecondary, 0.2),
                                  color: appTheme.colors.textSecondary,
                                  fontSize: isMobile ? '0.5rem' : '0.6rem',
                                  height: isMobile ? '16px' : '20px'
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </TableCell> */}

                    {/* ✅ EDITABLE PRICE EXCL VAT */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <EditableField
                        value={item.priceExclVAT || item.price}
                        field="priceExclVAT"
                        index={index}
                        type="number"
                        displayValue={`£${safeToFixed(item.priceExclVAT || item.price)}`}
                      />
                    </TableCell>

                    {/* ✅ AUTO-CALCULATED VAT AMOUNT (READ-ONLY) */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <Typography variant="body2" sx={{
                        color: item.hasVAT ? appTheme.colors.accent : appTheme.colors.textSecondary,
                        fontWeight: 500,
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>
                        £{safeToFixed(item.vatAmount || 0)}
                      </Typography>
                    </TableCell>

                    {/* ✅ AUTO-CALCULATED PRICE INCL VAT (READ-ONLY) */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: item.hasVAT ? appTheme.colors.secondary : appTheme.colors.textPrimary,
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>
                        £{safeToFixed(item.priceInclVAT || item.price)}
                      </Typography>
                    </TableCell>

                    {/* ✅ EDITABLE QUANTITY */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <EditableField
                        value={item.quantity}
                        field="quantity"
                        index={index}
                        type="number"
                      />
                    </TableCell>

                    {/* ✅ EDITABLE UNITS */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <EditableField
                        value={item.units}
                        field="units"
                        index={index}
                        type="select"
                        options={[
                          { value: 'pcs', label: 'Pieces' },
                          { value: 'kg', label: 'Kilograms' },
                          { value: 'g', label: 'Grams' },
                          { value: 'litres', label: 'Litres' },
                          { value: 'ml', label: 'Millilitres' },
                          { value: 'box', label: 'Box' },
                          { value: 'pack', label: 'Pack' }
                        ]}
                      />
                    </TableCell>

                    {/* ✅ AUTO-CALCULATED ITEM TOTAL (READ-ONLY) */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 700,
                        color: appTheme.colors.primary,
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>
                        £{safeToFixed(calculateItemTotal(item))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 💰 DISCOUNT CONTROLS - ADD THIS ENTIRE BLOCK */}
          {editableInvoice?.totalPrice ? (
            <Box sx={{ marginTop: 3, padding: 2, backgroundColor: alpha(appTheme.colors.warning, 0.05), borderRadius: 2 ,pageBreakInside: 'avoid',
            breakInside: 'avoid'}}>
              <Typography className="hide-in-pdf" variant="h6" sx={{ color: appTheme.colors.warning, mb: 2, fontWeight: 600 }}>
                💰 Discount (Optional)
              </Typography>
              <Grid container spacing={2} >
                <Grid className="hide-in-pdf" item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                      value={discountType}
                      label="Discount Type"
                      onChange={(e) => {
                        setDiscountType(e.target.value);
                        if (e.target.value === 'none') setDiscountValue('');
                      }}
                    >
                      <MenuItem value="none">No Discount</MenuItem>
                      <MenuItem value="percent">Percentage (%)</MenuItem>
                      <MenuItem value="amount">Fixed Amount (£)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid className="hide-in-pdf" item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={discountType === 'percent' ? 'Discount %' : 'Discount Amount (£)'}
                    type="number"
                    size="small"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    disabled={discountType === 'none'}
                    InputProps={{
                      startAdornment: discountType === 'percent' ? <InputAdornment position="start">%</InputAdornment> : null,
                    }}
                  />
                </Grid>

                <Grid className="hide-in-pdf" item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Reason (Optional)"
                    size="small"
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder="e.g. Loyalty discount, First order promo"
                  />
                </Grid>

                {discountType !== 'none' && discountValue && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, backgroundColor: alpha(appTheme.colors.success, 0.1), borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Original: £{safeToFixed(originalAmount)}
                      </Typography>
                      <Typography variant="h6" sx={{ color: appTheme.colors.success, fontWeight: 700 }}>
                        Discounted Total: £{safeToFixed(calculateDiscountedTotal(originalAmount))}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Reason: {discountReason}
                      </Typography>
                      <Typography variant="caption" sx={{ color: appTheme.colors.textSecondary }}>
                        {discountType === 'percent' ? `${discountValue}%` : `£${discountValue}`} discount
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : null}



          {/* TOTALS SECTION - Same as before */}
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'flex-start',
            marginBottom: isMobile ? theme.spacing(2) : appTheme.spacing.xl,
            gap: isMobile ? theme.spacing(2) : 0,
            marginTop: '20px',
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
            pageBreakBefore: 'auto',     // 👈 key
            paddingTop: '16px',
          }}>
            {/* Status Controls */}
            <Box sx={{ flex: 1, order: isMobile ? 2 : 1 }}>
              {!showDropdown ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: 2
                }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel sx={{
                      fontSize: isMobile ? '0.75rem' : '0.875rem'
                    }}>Order Status</InputLabel>
                    <Select
                      value={editableInvoice.orderStatus}
                      onChange={(e) => handleStatusChange("orderStatus", e.target.value)}
                      label="Order Status"
                      sx={{
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="accepted">Accepted</MenuItem>
                      <MenuItem value="shipped">Shipped</MenuItem>
                      <MenuItem value="delivered">Delivered</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel sx={{
                      fontSize: isMobile ? '0.75rem' : '0.875rem'
                    }}>Payment Status</InputLabel>
                    <Select
                      value={editableInvoice.isBillPaid ? "paid" : "pending"}
                      onChange={(e) => handleStatusChange("isBillPaid", e.target.value === "paid")}
                      label="Payment Status"
                      sx={{
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>
                    <strong>Order Status:</strong> {editableInvoice.orderStatus}
                  </Typography>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>
                    <strong>Payment Status:</strong> {editableInvoice.isBillPaid ? "Paid" : "Pending"}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* ✅ AUTO-CALCULATED TOTALS */}
            <Box sx={{
              minWidth: isMobile ? '100%' : '300px',
              padding: isMobile ? theme.spacing(2) : appTheme.spacing.lg,
              backgroundColor: appTheme.colors.surfaceLuxury,
              borderRadius: appTheme.borderRadius.medium,
              border: `2px solid ${appTheme.colors.primary}`,
              order: isMobile ? 1 : 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography variant="body1" sx={{
                  fontWeight: 500,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>Subtotal:</Typography>
                <Typography variant="body1" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  £{safeToFixed(calculateInvoiceTotals().subtotal)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography variant="body1" sx={{
                  fontWeight: 500,
                  color: appTheme.colors.accent,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  VAT (20%):
                </Typography>
                <Typography variant="body1" sx={{
                  fontWeight: 600,
                  color: appTheme.colors.accent,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  £{safeToFixed(calculateInvoiceTotals().tax)}
                </Typography>
              </Box>

              <Divider sx={{ margin: '8px 0' }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: appTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  Total:
                </Typography>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: appTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  £{safeToFixed(calculateInvoiceTotals().totalPrice)}
                </Typography>
              </Box>
              <Divider sx={{ margin: '8px 0' }} />
              {discountType !== 'none' && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: appTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  Discounted Total:
                </Typography>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: appTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  £{safeToFixed(calculateDiscountedTotal(originalAmount))}
                </Typography>
              </Box>}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* DIALOG ACTIONS - Same as before */}
      <DialogActions sx={{
        padding: isMobile ? theme.spacing(2) : appTheme.spacing.lg,
        backgroundColor: appTheme.colors.surfaceElevated,
        gap: isMobile ? 1 : 2,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Button
          onClick={handleClose}
          sx={{
            color: appTheme.colors.textSecondary,
            borderRadius: appTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: appTheme.colors.primary,
            borderRadius: appTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto',
            "&:hover": {
              backgroundColor: appTheme.colors.deepTeal,
            }
          }}
        >
          Save Changes
        </Button>
        <Button
          onClick={handleSendInvoice}
          variant="contained"
          sx={{
            backgroundColor: appTheme.colors.secondary,
            borderRadius: appTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto',
            "&:hover": {
              backgroundColor: appTheme.colors.warmCopper,
            }
          }}
        >
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceModal;
